import { DOCUMENT_PAGE_RENDERING_CANCELLED_EXCEPTION } from 'constants/documents';
import { openURLInCurrentTab, openURLInNewTab } from 'utils/navigation';

const getPdfData = pdf =>
  new Promise((resolve, reject) => {
    if (pdf) {
      pdf.getData().then(async pdfData => {
        const { PDFDocument } = await import('pdf-lib');

        PDFDocument.load(pdfData, { ignoreEncryption: true }).then(pdfLibDoc => {
          if (pdfLibDoc.isEncrypted) {
            reject('Selected document is encrypted and cannot be edited!');
          }

          resolve(pdfLibDoc);
        });
      });
    }
  });

export const mergePdfs = async pdfsToMerge => {
  const { PDFDocument } = await import('pdf-lib');
  const mergedPdf = await PDFDocument.create();

  for (const pdfCopyDoc of pdfsToMerge) {
    const pdfData = await getPdfData(pdfCopyDoc);
    const copiedPages = await mergedPdf.copyPages(pdfData, pdfData.getPageIndices());
    copiedPages?.forEach(page => {
      mergedPdf.addPage(page);
    });
  }

  return mergedPdf.save();
};

const setPdfJsWorker = async () => {
  const PdfJs = await import('pdfjs-dist');
  // TODO: research regarding using worker from local build, not available for current 4 version
  // const pdfjsWorker = await import('pdfjs-dist/build/pdf.worker.entry');

  PdfJs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PdfJs.version}/pdf.worker.min.mjs`;

  return PdfJs;
};

// The cdnjs worker package has no standard_fonts, so non-embedded standard-14 fonts load from the
// copy bundled into the build. With no cMapUrl set, pdf.js fetches them on the main thread.
export const getPdfJsDocument = async params => {
  const PdfJs = await setPdfJsWorker();

  return PdfJs.getDocument({
    ...params,
    standardFontDataUrl: process.env.PDFJS_STANDARD_FONTS_URL,
  });
};

export const checkIsPageRenderingCancelledException = err =>
  err?.name === DOCUMENT_PAGE_RENDERING_CANCELLED_EXCEPTION;

export const getPdfBlobFromBytes = encodedData => {
  const decodedData = atob(encodedData);
  const bytesData = new Uint8Array(decodedData.length);

  for (let i = 0; i < decodedData.length; i++) {
    bytesData[i] = decodedData.charCodeAt(i);
  }

  return new Blob([bytesData], { type: 'application/pdf' });
};

export const openPdfFromBytes = (encodedData, options = {}) => {
  const { shouldFocusTab = true, openInNewTab = true } = options;

  const blob = getPdfBlobFromBytes(encodedData);
  const fileURL = URL.createObjectURL(blob);

  if (openInNewTab) {
    return openURLInNewTab(fileURL, shouldFocusTab);
  }

  openURLInCurrentTab(fileURL);
};
